import { useTranslation } from '@dreamstack/i18n'
import { logger } from '@dreamstack/logger/src/logger'
import { trpc } from '@dreamstack/trpc/client'
import { useConfirmationDialog } from '../confirmation/useConfirmationDialog'
import { useSuccessDialog } from '../confirmation/useSuccessDialog'

export enum SocialShareOptions {
  WHATSAPP = 'Whatsapp',
  FACEBOOK = 'Facebook',
  TWITTER = 'Twitter',
}
export type SocialShareProps = {
  title: string
  url: string
  text?: string
}

export const shareWhatsapp = (url: string, text?: string) => {
  window.open(
    'https://api.whatsapp.com/send?text=' +
      (text ?? '') +
      ' ' +
      process.env.NEXT_PUBLIC_NEXT_BASE_URL +
      url,
    '_blank'
  )
}

export const shareFacebook = (url: string) => {
  window.open(
    'https://www.facebook.com/share.php?u=' +
      process.env.NEXT_PUBLIC_NEXT_BASE_URL +
      url,
    '_blank'
  )
}

export const shareTwitter = (url: string, text?: string) => {
  window.open(
    'https://twitter.com/intent/tweet?text=' +
      (text ?? '') +
      '&url=' +
      process.env.NEXT_PUBLIC_NEXT_BASE_URL +
      url,
    '_blank'
  )
}

/**
 * Usage: 
 * ```
  const { confirmationDialog, successDialog } = useNotInterestedConfirmation({
    propertyId: props.propertyId ?? 0,
  })

  return <>
      {confirmationDialog.ConfirmationDialog}
      {successDialog.SuccessDialog}
      ...
      <SimpleMuiButton
        variant="text"
        color="primary"
        size="small"
        onClick={() => confirmationDialog.requestConfirmation()}
      >
        Kein Interesse
      </SimpleMuiButton>
  </>
 * ```
 */
export const useNotInterestedConfirmation = ({
  propertyId,
}: {
  propertyId: number
}) => {
  const t = useTranslation()

  const { mutateAsync: notInterestedBusinessLogic } =
    trpc.contact.notInterested.useMutation()

  const successDialog = useSuccessDialog({
    title: t('accentro:dashboard.timeline.notInterested.successTitle'),
    text: t('accentro:dashboard.timeline.notInterested.successText'),
    ctaLabel: t('accentro:close'),
  })

  const confirmationDialog = useConfirmationDialog({
    title: t('accentro:dashboard.timeline.notInterested.modalTitle'),
    text: t('accentro:dashboard.timeline.notInterested.modalText'),
    deleteConfirmation: true,
    deleteConfirmationText: t(
      'accentro:dashboard.timeline.notInterested.removeObjectLabel'
    ),
    callback: async () => {
      await notInterestedBusinessLogic({ propertyId })
      successDialog.show()
    },
  })

  return {
    confirmationDialog,
    successDialog,
  }
}

export const useReservationConfirmationDialog = ({
  propertyId,
}: {
  propertyId: number | null
}) => {
  const t = useTranslation()
  const utils = trpc.useContext()
  const { mutateAsync: setDealStageToPrereservation } =
    trpc.contact.setDealStageToPrereservation.useMutation({
      onSettled: () => {
        utils.contact.getDealStageStep.invalidate({
          propertyId: propertyId ?? undefined,
        })
      },
      onError: (e) => {
        logger.error(e.message, e.data)
      },
    })
  const {
    ConfirmationDialog: ReserveDialog,
    requestConfirmation: requestReservationConfirmation,
  } = useConfirmationDialog({
    title: t(
      'accentro:dashboard.timeline.statuses.prereservation_reserveProperty'
    ),
    text: t(
      'accentro:dashboard.timeline.statuses.prereservation_reservePropertyText'
    ),
    confirmationText: t(
      'accentro:dashboard.timeline.statuses.prereservation_reservePropertyConfirm'
    ),

    callback: async () => {
      await setDealStageToPrereservation({
        propertyId: propertyId ?? 0,
      })
    },
  })

  return {
    ReserveDialog,
    requestReservationConfirmation,
  }
}
